import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";

// Client-side canvas story card (1080x1920). No backend needed.
export function StoryCard({ user }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !user) return;
    (async () => {
      try { await document.fonts.ready; } catch {}
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const W = 1080, H = 1920;
      canvas.width = W; canvas.height = H;

      // Background gradient (warm deep red -> near black)
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#1a0505");
      bg.addColorStop(1, "#0a0605");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // Radial glow
      const rg = ctx.createRadialGradient(W/2, H*0.28, 120, W/2, H*0.28, 900);
      rg.addColorStop(0, "rgba(220,38,38,0.32)");
      rg.addColorStop(1, "rgba(220,38,38,0)");
      ctx.fillStyle = rg;
      ctx.fillRect(0, 0, W, H);

      // Grain
      ctx.globalAlpha = 0.05;
      ctx.fillStyle = "#fff";
      for (let i = 0; i < 400; i++) {
        ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
      }
      ctx.globalAlpha = 1;

      // Wordmark
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.font = '700 68px "Outfit", system-ui, sans-serif';
      const nextW = ctx.measureText("Next").width;
      const sceneW = ctx.measureText("Scene").width;
      const totalW = nextW + sceneW;
      const startX = W/2 - totalW/2;
      ctx.textAlign = "left";
      ctx.fillStyle = "#fff";
      ctx.fillText("Next", startX, 180);
      ctx.fillStyle = "#DC2626";
      ctx.fillText("Scene", startX + nextW, 180);

      // Verified circle badge
      ctx.textAlign = "center";
      ctx.beginPath();
      ctx.arc(W/2, 580, 140, 0, Math.PI*2);
      ctx.fillStyle = "#DC2626";
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 4;
      ctx.stroke();

      // Checkmark
      ctx.beginPath();
      ctx.moveTo(W/2 - 65, 585);
      ctx.lineTo(W/2 - 15, 635);
      ctx.lineTo(W/2 + 70, 535);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 18;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();

      // Name
      const name = (user.name || "").trim();
      ctx.fillStyle = "#fff";
      ctx.font = '700 96px "Outfit", system-ui, sans-serif';
      ctx.fillText(name.length > 22 ? name.slice(0,22) + "…" : name, W/2, 860);

      // Username
      ctx.font = '400 44px "Manrope", system-ui, sans-serif';
      ctx.fillStyle = "#FCA5A5";
      ctx.fillText("@" + (user.username || ""), W/2, 928);

      // Category
      const cat = (user.category || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      if (cat) {
        ctx.font = '500 56px "Outfit", system-ui, sans-serif';
        ctx.fillStyle = "#F5C462";
        ctx.fillText(cat, W/2, 1040);
      }

      // City if present
      const city = user.profile?.city;
      if (city) {
        ctx.font = '400 40px "Manrope", system-ui, sans-serif';
        ctx.fillStyle = "rgba(255,255,255,0.65)";
        ctx.fillText(city, W/2, 1110);
      }

      // "VERIFIED ON" banner
      ctx.font = '500 48px "Outfit", system-ui, sans-serif';
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.fillText("VERIFIED ON", W/2, 1380);

      ctx.font = '800 132px "Outfit", system-ui, sans-serif';
      ctx.fillStyle = "#DC2626";
      ctx.fillText("NEXTSCENE", W/2, 1520);

      // Bottom CTA
      ctx.font = '400 38px "Manrope", system-ui, sans-serif';
      ctx.fillStyle = "#a1a1aa";
      ctx.fillText("Bharat's professional talent stage", W/2, 1770);

      ctx.font = '600 46px "Manrope", system-ui, sans-serif';
      ctx.fillStyle = "#fff";
      ctx.fillText("Join at nextscene.in", W/2, 1840);
    })();
  }, [user]);

  const download = () => {
    if (!canvasRef.current) return;
    const link = document.createElement("a");
    link.download = `nextscene-verified-${user?.username || "card"}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
    toast.success("Story card downloaded");
  };

  const share = async () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `nextscene-${user?.username || "card"}.png`, { type: "image/png" });
      try {
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Verified on NextScene",
            text: `Now verified on NextScene ✓ Join Bharat's talent stage → nextscene.in`,
          });
          return;
        }
      } catch {}
      download();
    }, "image/png");
  };

  return (
    <div>
      <div className="rounded-2xl overflow-hidden border border-zinc-700 shadow-2xl shadow-[#DC2626]/25 mx-auto max-w-[280px]">
        <canvas ref={canvasRef} className="block w-full h-auto"/>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Button data-testid="story-download" onClick={download} className="rounded-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700">
          <Download size={14} className="mr-2"/> Download
        </Button>
        <Button data-testid="story-share" onClick={share} className="rounded-full h-11 bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          <Share2 size={14} className="mr-2"/> Share
        </Button>
      </div>
    </div>
  );
}
